// Eén bron voor "wat nu?" in de Offerte Generator.
//
// Vóór deze module leidden vier plekken onafhankelijk af wat de volgende stap
// was (de StepStrip, een IIFE in de chat, de knoptekst van createInGripp en de
// waarschuwings-alerts) — en die konden elkaar tegenspreken. Alles wat de UI
// over de voortgang wil zeggen komt nu hieruit.
//
// Puur: geen React, geen fetch, geen Date. Alleen input → output, zodat het
// testbaar is (offerteFlow.test.js) en de container de enige plek blijft waar
// state leeft.

/**
 * @param {object} s De relevante container-state.
 * @returns {{ stage: string, statusDot: {label: string, color: string},
 *            primary: object|null, secondary: object[], badges: object[],
 *            focus: string|null }}
 */
export function nextAction(s = {}) {
  const {
    started = false,
    draft = null,
    company = null,
    isExistingOffer = false,
    createResult = null,
    editingOffer = null,
    hasUnsavedGrippChanges = false,
    clientAgreement = null,
    priceCheckWarnings = 0,
    englishGaps = 0,
    missingGrippFields = [],
    busy = {},
  } = s;

  const lines = (draft && draft.lines) || [];
  const hasLines = lines.length > 0;
  const inGripp = !!(createResult && createResult.ok);
  const offerLabel = inGripp
    ? (createResult.offerNumber ? `offerte ${createResult.offerNumber}` : `offerte #${createResult.offerId || '?'}`)
    : (editingOffer ? `offerte ${editingOffer.number}` : 'concept');

  const badges = [];
  if (draft && Array.isArray(draft.vragen) && draft.vragen.length > 0) {
    badges.push({
      key: 'vragen', severity: 'warning',
      label: `${draft.vragen.length} ${draft.vragen.length === 1 ? 'vraag' : 'vragen'}`,
    });
  }
  if (draft && draft.notes) {
    badges.push({ key: 'notities', severity: 'info', label: 'notitie van de assistent' });
  }
  if (clientAgreement) {
    badges.push({
      key: 'afspraken', severity: 'success',
      label: `prijsafspraken ${clientAgreement.company_name || 'actief'}`,
    });
  }
  if (priceCheckWarnings > 0) {
    badges.push({
      key: 'prijscheck', severity: 'warning',
      label: `${priceCheckWarnings} ${priceCheckWarnings === 1 ? 'prijsafwijking' : 'prijsafwijkingen'}`,
    });
  }
  if (draft && missingGrippFields.length > 0) {
    badges.push({
      key: 'verplicht', severity: 'warning',
      label: `${missingGrippFields.length} verplichte ${missingGrippFields.length === 1 ? 'veld' : 'velden'} leeg`,
    });
  }
  if (englishGaps > 0) {
    badges.push({
      key: 'engels', severity: 'info',
      label: `${englishGaps} ${englishGaps === 1 ? 'regel' : 'regels'} zonder Engels product`,
    });
  }
  if (inGripp && hasUnsavedGrippChanges) {
    badges.push({ key: 'nietOpgeslagen', severity: 'warning', label: 'wijzigingen nog niet in Gripp' });
  }
  if (inGripp && !hasUnsavedGrippChanges) {
    badges.push({ key: 'inGripp', severity: 'success', label: `in Gripp — ${offerLabel}` });
  }

  // Statusbolletje voor de statusregel.
  const statusDot = inGripp
    ? (hasUnsavedGrippChanges
      ? { label: 'Gewijzigd na Gripp', color: '#d97706' }
      : { label: 'In Gripp', color: '#059669' })
    : { label: editingOffer ? `Bewerkt: ${offerLabel}` : 'Concept', color: '#4f46e5' };

  // ── De volgorde-takken, van vroeg naar laat in het proces ──
  if (!started) {
    return {
      stage: 'intake', statusDot, badges, focus: null,
      primary: { key: 'start', label: 'Start gesprek met de assistent' },
      secondary: [],
    };
  }

  if (!draft) {
    return {
      stage: 'gesprek', statusDot, badges, focus: 'chat',
      primary: {
        key: 'antwoord', label: 'Beantwoord de vraag van de assistent',
        busy: !!busy.chat, busyLabel: 'Assistent denkt na…',
      },
      secondary: [{ key: 'forceDraft', label: 'Maak nu alvast een concept (met wat bekend is)' }],
    };
  }

  if (!hasLines) {
    return {
      stage: 'regels', statusDot, badges, focus: null,
      primary: {
        key: 'genLines', label: 'Genereer offerteregels',
        busy: !!busy.lines, busyLabel: 'Regels genereren…',
        disabled: !draft.description,
        reason: !draft.description ? 'Zonder omschrijving kan ik geen regels afleiden' : null,
      },
      // Bestaand gedrag: een concept met alléén een omschrijving mag ook naar Gripp.
      secondary: [{ key: 'gripp', label: 'Maak concept in Gripp (alleen omschrijving)' }],
    };
  }

  if (!isExistingOffer && !(company && company.id)) {
    return {
      stage: 'klant', statusDot, badges, focus: 'company',
      // Geen grijze knop met een tooltip, maar een actie die het veld opent:
      // een uitgeschakelde knop zonder uitleg was precies de klacht.
      primary: { key: 'kiesKlant', label: 'Kies de klant' },
      secondary: [{ key: 'check', label: 'Controleer offerte', busy: !!busy.checking, busyLabel: 'Controleren…' }],
    };
  }

  if (!inGripp) {
    return {
      stage: 'controle', statusDot, badges, focus: null,
      primary: {
        key: 'gripp', label: 'Maak concept-offerte in Gripp',
        busy: !!busy.creating, busyLabel: 'Aanmaken in Gripp…',
      },
      secondary: [{ key: 'check', label: 'Controleer offerte', busy: !!busy.checking, busyLabel: 'Controleren…' }],
    };
  }

  if (hasUnsavedGrippChanges) {
    return {
      stage: 'bijwerken', statusDot, badges, focus: null,
      primary: {
        key: 'gripp',
        label: `Werk ${offerLabel} bij in Gripp`,
        busy: !!busy.creating, busyLabel: 'Bijwerken in Gripp…',
      },
      // De offerte stáát al in Gripp — er meteen naartoe kunnen mag niet
      // afhangen van of je eerst je wijzigingen doorzet.
      secondary: [
        { key: 'openGripp', label: 'Open in Gripp' },
        { key: 'check', label: 'Controleer offerte', busy: !!busy.checking, busyLabel: 'Controleren…' },
      ],
    };
  }

  return {
    stage: 'klaar', statusDot, badges, focus: null,
    primary: { key: 'openGripp', label: 'Open in Gripp' },
    secondary: [
      { key: 'volgende', label: 'Volgende offerte uit dit gesprek' },
      { key: 'check', label: 'Controleer offerte', busy: !!busy.checking, busyLabel: 'Controleren…' },
    ],
  };
}

/**
 * Welke verplichte Gripp-velden zijn nog leeg? (De klant gaat apart — die heeft
 * een eigen stage in nextAction.)
 */
export function missingGrippFields(draft, fieldOptions) {
  if (!draft) return [];
  return Object.keys(fieldOptions).filter((f) => !fieldOptions[f].includes(draft[f]));
}
